import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { CloudSyncOutlined, SettingOutlined } from '@ant-design/icons';
import { Row, Select, message } from 'antd';
import axios from 'axios';

import WorkflowHandle from './WorkflowHandle';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowSageIntacctCreateJournalConfigureDrawer from './WorkflowSageIntacctCreateJournalConfigureDrawer';
import useToggle from '../../common/hooks/useToggle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import { getType, updateData } from './workflowHelpers';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the following fields to be configured: <br />
      - <b>Journal</b> <br />
      - <b>Line Item Table Field</b> (set via <b>Configure</b>) <br />
      - <b>Amount Field</b> (set via <b>Configure</b>) <br />
      - <b>Cost GL Account</b> <br />
      - <b>Revenue GL Account</b> <br />
    </div>
  ),
};

const DISABLED_TEXT = 'Add at least one equipment table or time tracking grid';

const HOVER_TEXT = (
  <div>
    Create a Journal Entry in Sage Intacct from table line items
  </div>
);

const LINE_TABLE_TYPES = new Set(['table - Equipment', 'timeTrackingGrid']);

export default function WorkflowSageIntacctCreateJournalNode({
  setDataMap,
  setElements,
  isDisplay,
  name,
  sections = [],
  divisionId,
  setDisableNext,
} = {}) {
  return function _({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const {
      fieldMappings: initialFieldMappings = {},
      journal: initialJournal = null,
      costGlAccount: initialCostGlAccount = null,
      revenueGlAccount: initialRevenueGlAccount = null,
    } = data;

    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [glAccounts, setGlAccounts] = useState([]);
    const [journals, setJournals] = useState([]);
    const [prevDivisionId, setPrevDivisionId] = useState();
    const [journal, setJournal] = useState(initialJournal);
    const [costGlAccount, setCostGlAccount] = useState(initialCostGlAccount);
    const [revenueGlAccount, setRevenueGlAccount] = useState(initialRevenueGlAccount);

    const {
      isToggled: isMappingDrawerVisible,
      toggle: toggleMappingDrawer,
    } = useToggle(false);

    const selectedLineTableType = useMemo(() => {
      if (!fieldMappings?.lineTable) return null;

      const field = sections
        .flatMap((section) => section.fields || [])
        .find(({ id: fieldId }) => fieldId === fieldMappings.lineTable);

      return field ? getType(field) : null;
    }, [sections, fieldMappings?.lineTable]);

    const requiresAmountMapping = selectedLineTableType !== 'timeTrackingGrid';

    const disableNext = (
      !journal
      || !fieldMappings?.lineTable
      || (requiresAmountMapping && !fieldMappings?.amount)
      || !costGlAccount
      || !revenueGlAccount
    );

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [setDisableNext, id, disableNext]);

    const onGlAccountChange = useCallback((key) => (newValue) => {
      if (!setDataMap || !id) return;
      if (key === 'costGlAccount') {
        setCostGlAccount(newValue);
      } else {
        setRevenueGlAccount(newValue);
      }
      setDataMap(updateData(id, { [key]: newValue }));
    }, [id, setDataMap]);

    const onJournalChange = useCallback((key) => (newValue) => {
      if (!setDataMap || !id) return;
      setJournal(newValue);
      setDataMap(updateData(id, { [key]: newValue }));
    }, [id, setDataMap]);

    const getGlAccounts = async () => {
      try {
        const {
          data: {
            journals: returnedJournals = [],
            glAccounts: returnedGlAccounts = [],
          } = {},
        } = await axios.get(
          '/sage/intacct/journalsAndAccounts',
          {
            params: {
              divisionId,
            },
          },
        );
        setJournals(Array.isArray(returnedJournals) ? returnedJournals : []);
        setGlAccounts(Array.isArray(returnedGlAccounts) ? returnedGlAccounts : []);
      } catch (err) {
        message.error('Error getting GL accounts');
      }
    };

    const onFieldMappingsChange = (newMappings) => {
      if (!setDataMap || !id) return;
      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    };

    const lineTableFields = useMemo(() => (
      sections.reduce((sAcc, section) => {
        const { fields = [] } = section;
        return sAcc.concat(
          fields.reduce((fAcc, field) => {
            const { id: fieldId, configProps: { title } = {} } = field;
            const type = getType(field);
            if (LINE_TABLE_TYPES.has(type)) {
              fAcc.push({ label: title, value: fieldId });
            }
            return fAcc;
          }, []),
        );
      }, [])
    ), [sections]);

    const glAccountOptions = useMemo(() => (
      glAccounts.map(({ sageId, number, name: accountName }) => ({
        value: sageId,
        label: number ? `${number} - ${accountName}` : accountName,
      }))
    ), [glAccounts]);

    const journalOptions = useMemo(() => (
      journals.map(({ sageId, number, name: journalName }) => ({
        value: sageId,
        label: number ? `${number} - ${journalName}` : journalName,
      }))
    ), [journals]);

    const errorDetails = !journal
      || !fieldMappings?.lineTable
      || (requiresAmountMapping && !fieldMappings?.amount)
      || !costGlAccount
      || !revenueGlAccount
      ? ERROR_DETAILS
      : undefined;

    if (divisionId && prevDivisionId !== divisionId) {
      setPrevDivisionId(divisionId);
      getGlAccounts();
    }

    return (
      <div style={{ position: 'relative' }}>
        <WorkflowActionNode
          title="Create Journal in Sage Intacct"
          Icon={CloudSyncOutlined}
          draggable={draggable}
          onNodeUpdate={(...args) => {
            setElements(...args);
            if (setDisableNext) {
              setDisableNext((prev) => ({ ...prev, [id]: false }));
            }
          }}
          isDisplay={isDisplay || disabled}
          errorPopover={errorDetails}
          hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
          style={disabled ? { opacity: 0.7 } : {}}
          type="sageIntacctCreateJournal"
          id={id}
        >
          {!draggable && (
            <div>
              <WorkflowHandle type="target" position="top" disabled={isDisplay} />
              <Row style={{ marginTop: 10 }}>
                Select Journal
              </Row>
              <Row style={{ margin: '10px 0px' }}>
                <Select
                  onChange={onJournalChange('journal')}
                  value={journal}
                  showSearch
                  optionFilterProp="label"
                  options={journalOptions}
                  style={{ width: '100%' }}
                />
              </Row>
              <Row style={{ marginTop: 10 }}>
                Select Cost GL Account
              </Row>
              <Row style={{ margin: '10px 0px' }}>
                <Select
                  onChange={onGlAccountChange('costGlAccount')}
                  value={costGlAccount}
                  showSearch
                  optionFilterProp="label"
                  options={glAccountOptions}
                  style={{ width: '100%' }}
                />
              </Row>
              <Row style={{ marginTop: 10 }}>
                Select Revenue GL Account
              </Row>
              <Row style={{ margin: '10px 0px' }}>
                <Select
                  onChange={onGlAccountChange('revenueGlAccount')}
                  value={revenueGlAccount}
                  showSearch
                  optionFilterProp="label"
                  options={glAccountOptions}
                  style={{ width: '100%' }}
                />
              </Row>
              <Row style={{ margin: '20px 0px' }}>
                <BorderlessButton
                  title="Configure"
                  style={{ margin: 5 }}
                  iconNode={<SettingOutlined />}
                  onClick={toggleMappingDrawer}
                />
              </Row>
            </div>
          )}
        </WorkflowActionNode>
        <WorkflowSageIntacctCreateJournalConfigureDrawer
          visible={isMappingDrawerVisible}
          onClose={toggleMappingDrawer}
          onSubmit={toggleMappingDrawer}
          sourceName={name}
          sourceSections={sections}
          fieldMappings={fieldMappings}
          onFieldMappingsChange={onFieldMappingsChange}
          lineTableFields={lineTableFields}
        />
      </div>
    );
  };
}
